import classNames from "classnames";
import { Checkbox as Ch } from "primereact/checkbox";
import styles from "./Checkbox.module.css";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

export interface ICheckbox {
  id: string | number;
  label: string;
  checked: boolean;
  description?: string;
  disabled?: boolean;
}

export interface CheckboxProps {
  checked: boolean;
  onChange?(arg0: any): void;
  label?: string;
  disabled?: boolean;
  description?: string;
  fullWidth?: boolean;
  isInsideDropdown?: boolean;
  focused?: boolean;
  style?: React.CSSProperties;
  innerStyle?: React.CSSProperties;
  id?: string;
  htmlFor?: string;
  labelStyles?: React.CSSProperties;
  dataCy?: string;
  tooltip?: boolean;
  className?: string;
  labelClassName?: string;
  tabIndex?: 0 | 1 | -1;
  ariaSelected?: boolean;
  ariaLabel?: string;
}

export default function Checkbox({
  checked,
  onChange = () => {},
  label,
  disabled,
  description,
  fullWidth,
  isInsideDropdown = false,
  style,
  focused,
  innerStyle,
  id,
  htmlFor,
  labelStyles,
  dataCy,
  tooltip,
  className,
  labelClassName,
  tabIndex,
  ariaSelected,
  ariaLabel,
}: Readonly<CheckboxProps>) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const itemClasses = classNames(className, {
    [styles.checkbox]: true,
    [styles.focused]: focused,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const checkboxWrapper =
      containerRef.current.querySelector<HTMLElement>(".p-checkbox");
    if (!checkboxWrapper) return;

    const input = checkboxWrapper.querySelector<HTMLInputElement>(
      "input[type='checkbox']",
    );
    if (!input) return;

    // Remove ARIA attributes from wrapper div (WCAG requirement)
    // The wrapper div should not have interactive ARIA attributes
    checkboxWrapper.removeAttribute("aria-label");
    checkboxWrapper.removeAttribute("aria-labelledby");
    checkboxWrapper.removeAttribute("aria-checked");
    checkboxWrapper.removeAttribute("aria-selected");

    // Apply aria-label to the actual input element when provided
    // This ensures WCAG 4.1.2 compliance when label is hidden
    if (ariaLabel) {
      input.setAttribute("aria-label", ariaLabel);
    } else {
      // If no ariaLabel, remove it from input (label element provides name via htmlFor)
      input.removeAttribute("aria-label");
    }
  }, [ariaLabel, label, checked, ariaSelected]);

  return (
    <div className={`${itemClasses} option`} style={style} data-cy={dataCy}>
      <div
        className={styles.mainInfo}
        style={{ ...innerStyle }}
        ref={containerRef}
      >
        <Ch
          id={id}
          tabIndex={tabIndex}
          inputId={htmlFor ?? label}
          name={label}
          value={label}
          onChange={onChange}
          checked={checked}
          className={styles.checkboxContainer}
          disabled={disabled}
          tooltipOptions={{ position: "top" }}
          tooltip={tooltip ? t("Common:select_all_below") : undefined}
          aria-selected={ariaSelected}
        />

        {label && (
          <label
            onChange={onChange}
            htmlFor={htmlFor ?? label}
            className={classNames(labelClassName, styles.checkboxLabel)}
            style={{
              width: fullWidth ? "100%" : "auto",
              marginLeft: isInsideDropdown ? "8px" : "12px",
              ...labelStyles,
            }}
          >
            {label}
          </label>
        )}
      </div>
      {description && <div className={styles.description}>{description}</div>}
    </div>
  );
}
